import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { statusBadge, ASSET_ICONS, ASSET_LABELS, ASSET_TYPES, STATUSES, formatDate } from '../utils';
import TransferModal from '../components/TransferModal';
import AddAssetModal from '../components/AddAssetModal';
import DamageModal from '../components/DamageModal';
import SortableTh, { useTableSort } from '../components/SortableTh';
import useDebouncedValue from '../hooks/useDebouncedValue';

export default function Assets() {
  const { user, withAuth } = useAuth();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  // The fetch keys off the settled value; the input stays bound to `search`.
  const debouncedSearch = useDebouncedValue(search);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [locations, setLocations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [transferAsset, setTransferAsset] = useState(null);
  const [damageAsset, setDamageAsset] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const { sort, dir, toggle } = useTableSort('updated', 'desc');

  const canEdit = ['it_admin', 'it_staff', 'supervisor'].includes(user?.role);
  const canAdd  = ['it_admin', 'it_staff'].includes(user?.role);

  // Load locations and hierarchy groups for the filter dropdowns
  useEffect(() => {
    withAuth(async () => {
      const [locRes, groupRes] = await Promise.all([
        api.getLocations(),
        api.getLocationGroups(),
      ]);
      setLocations((locRes.data || []).sort((a, b) => a.name.localeCompare(b.name)));
      setGroups(groupRes.data || []);
    });
  }, [withAuth]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await withAuth(async () => {
        const res = await api.getAssets({
          page, limit: 50, sort, dir,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          ...(filterType ? { type: filterType } : {}),
          ...(filterStatus ? { status: filterStatus } : {}),
          ...(filterLocation ? { locationId: filterLocation } : {}),
          ...(filterGroup ? { hierarchyGroup: filterGroup } : {}),
        });
        setAssets(res.data || []);
        setTotal(res.total || 0);
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, filterType, filterStatus, filterLocation, filterGroup, sort, dir, withAuth]);

  useEffect(() => { load(); }, [load]);

  // Sorting spans every page, so a page number from the old ordering no longer
  // points at anything meaningful.
  function handleSort(key) {
    toggle(key);
    setPage(1);
  }

  const sortProps = { sort, dir, onSort: handleSort };

  return (
    <div>
      {/* Header */}
      <div style={{ padding: '20px 28px', borderBottom: '2px solid var(--accent)', background: 'var(--bg-1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 600 }}>Assets</h1>
          <p style={{ color: 'var(--text-2)', fontSize: 13, marginTop: 2 }}>{total} total devices</p>
        </div>
        {canAdd && (
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add Asset</button>
        )}
      </div>

      {/* Filters */}
      <div style={{ padding: '14px 28px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, flexWrap: 'wrap', background: 'var(--bg-1)' }}>
        <input
          placeholder="Search name, serial, or tag…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{ width: 260 }}
        />
        {/* Every filter resets to page 1 — narrowing the set while on page 5
            could otherwise land on a page that no longer exists. */}
        <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }} style={{ width: 160 }}>
          <option value="">All Types</option>
          {ASSET_TYPES.map(t => <option key={t} value={t}>{ASSET_LABELS[t]}</option>)}
        </select>
        <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }} style={{ width: 160 }}>
          <option value="">All Status</option>
          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={filterGroup} onChange={e => { setFilterGroup(e.target.value); setPage(1); }} style={{ width: 180 }}>
          <option value="">All Regions</option>
          {groups.map(g => <option key={g.id} value={g.name}>{g.name}</option>)}
        </select>
        <select value={filterLocation} onChange={e => { setFilterLocation(e.target.value); setPage(1); }} style={{ width: 200 }}>
          <option value="">All Sites</option>
          <option value="unassigned">⚠ Unassigned</option>
          {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        {(search || filterType || filterStatus || filterLocation || filterGroup) && (
          <button className="btn btn-sm" onClick={() => {
            setSearch(''); setFilterType(''); setFilterStatus('');
            setFilterLocation(''); setFilterGroup(''); setPage(1);
          }}>
            Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div style={{ padding: '20px 28px' }}>
        <div className="card">
          {loading
            ? <div className="empty-state"><div className="spinner" /></div>
            : assets.length === 0
              ? <div className="empty-state">No assets found</div>
              : (
                <table>
                  <thead>
                    <tr>
                      <SortableTh label="Asset"       sortKey="name"     {...sortProps} />
                      <SortableTh label="Type"        sortKey="type"     {...sortProps} />
                      <SortableTh label="Tag / Serial" sortKey="tag"     {...sortProps} />
                      <SortableTh label="Status"      sortKey="status"   {...sortProps} />
                      <SortableTh label="Assigned To" sortKey="assigned" {...sortProps} />
                      <SortableTh label="Location"    sortKey="location" {...sortProps} />
                      <SortableTh label="Acquired"    sortKey="acquired" {...sortProps} />
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {assets.map(a => {
                      const sb = statusBadge(a.status);
                      return (
                        <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/assets/${a.id}`)}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 18 }}>{ASSET_ICONS[a.asset_type]}</span>
                              <div>
                                <div style={{ fontWeight: 500 }}>{a.display_name}</div>
                                <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{a.model}</div>
                              </div>
                            </div>
                          </td>
                          <td><span className="chip">{ASSET_LABELS[a.asset_type]}</span></td>
                          <td>
                            <div className="mono">{a.asset_tag}</div>
                            {a.serial_number && <div style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--mono)' }}>{a.serial_number}</div>}
                          </td>
                          <td><span className={`badge ${sb.className}`}>{sb.label}</span></td>
                          <td style={{ color: a.employee_name ? 'var(--text-0)' : 'var(--text-2)', fontSize: 14 }}>
                            {a.employee_name || 'Unassigned'}
                          </td>
                          <td style={{ color: 'var(--text-2)', fontSize: 14 }}>{a.location_name || '—'}</td>
                          <td style={{ color: 'var(--text-2)', fontSize: 13 }}>{formatDate(a.acquired_date)}</td>
                          <td onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', gap: 2 }}>
                              {canEdit && (
                                <button className="btn-icon" title="Transfer" onClick={() => setTransferAsset(a)}>⇄</button>
                              )}
                              {canEdit && (
                                <button className="btn-icon" title="Report Damage" onClick={() => setDamageAsset(a)}>⚠</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
          }
        </div>

        {/* Pagination */}
        {total > 50 && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
            <button className="btn btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span style={{ padding: '4px 12px', fontSize: 14, color: 'var(--text-2)' }}>
              Page {page} of {Math.ceil(total / 50)}
            </span>
            <button className="btn btn-sm" disabled={page >= Math.ceil(total / 50)} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </div>

      {/* Modals */}
      {transferAsset && (
        <TransferModal
          asset={transferAsset}
          onClose={() => setTransferAsset(null)}
          onSuccess={() => { setTransferAsset(null); load(); }}
        />
      )}
      {damageAsset && (
        <DamageModal
          asset={damageAsset}
          onClose={() => setDamageAsset(null)}
          onSuccess={() => { setDamageAsset(null); load(); }}
        />
      )}
      {showAdd && (
        <AddAssetModal
          onClose={() => setShowAdd(false)}
          onSuccess={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
}
